import { useState } from "react";
import { FileText, UploadCloud, X } from "lucide-react";
import { buildPayload, resolveCity, useSubmit, validateContact } from "../../lib/forms";
import { Wizard } from "./Wizard";
import { ChoiceCards, ConsentBlock, ConsentRow, ContactFields, Field, Group, Honeypot, MultiChips, NativeSelect, SuccessPanel, TextArea, TextField } from "./FormBits";
import { CITIES } from "../../lib/options";
import { Notice } from "../site/Blocks";

export const ADMIN_ROLE = "Administrative Coordinator (intake and office)";
export const ROLES = [
  "Caregiver / PSW (In-Home)",
  "Residential Support Worker (Independent Living)",
  ADMIN_ROLE,
];
const WORK_REGIONS = [
  { value: "gta", label: "Greater Toronto Area", desc: "Ontario, Canada" },
  { value: "other", label: "Outside the GTA", desc: "Tell us where you live" },
];
const YEARS = ["No paid experience yet", "Less than 1 year", "1–2 years", "3–5 years", "6–10 years", "More than 10 years"];
const AREAS = ["Seniors", "Personal care", "Mobility and transfers", "Companionship", "Dementia support", "Meal preparation",
  "Light housekeeping", "Medication reminders", "Disability support", "Overnight care", "Palliative or end-of-life support",
  "Youth (ages 16–24)", "Children"];
const CERTS = ["PSW certificate", "Developmental Services Worker (DSW)", "Child and Youth Care / Worker (CYC/CYW)",
  "Early Childhood Educator (ECE)", "Social Service Worker (SSW)", "Mental Health and Addictions Worker",
  "Youth mental health training", "Non-violent crisis intervention (CPI)", "First Aid / CPR", "Food handler certificate",
  "Dementia care training", "Valid Vulnerable Sector Check"];
const DAYS = ["Weekdays", "Weekends", "Holidays"];
const SHIFTS = ["Mornings", "Afternoons", "Evenings", "Overnight", "Live-in"];
const EMPLOYMENT = ["Full-time", "Part-time", "Casual / on-call", "Contract"];
const AUTH = ["Yes, I am authorized to work in the country of my preferred region", "No", "I would prefer to discuss"];

const INITIAL = {
  region: "", city: "", city_custom: "", preferred_role: "", years_experience: "", experience_summary: "",
  experience_areas: [], certifications: [], certifications_other: "", certification_details: "", availability_days: [],
  shifts: [], employment_types: [], hours_per_week: "", earliest_start: "", languages: "", work_authorization: "",
  name: "", email: "", phone: "", preferred_contact: "", contact_consent: false, privacy_consent: false,
  marketing_consent: false, website: "",
};
const MAX_BYTES = 5 * 1024 * 1024;

const ResumeInput = ({ file, setFile, setErr }) => {
  const pick = (f) => {
    if (!f) return;
    if (!/\.(pdf|docx?|DOCX?|PDF)$/.test(f.name)) return setErr("Resume must be a PDF, DOC or DOCX file.");
    if (f.size > MAX_BYTES) return setErr("Resume must be 5 MB or smaller.");
    setErr("");
    setFile(f);
  };
  return file ? (
    <div className="flex items-center justify-between rounded-xl border border-[#E5E0D8] bg-white px-4 py-3" data-testid="caregiver-resume-selected">
      <span className="flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-[#C86D51]" />{file.name}</span>
      <button type="button" onClick={() => setFile(null)} aria-label="Remove resume" data-testid="caregiver-resume-remove"><X className="h-4 w-4" /></button>
    </div>
  ) : (
    <label htmlFor="caregiver-resume" className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[#CFC8BC] bg-white px-6 py-8 text-center cursor-pointer hover:border-[#1E3A2B] transition-colors"
      onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}>
      <UploadCloud className="h-7 w-7 text-[#1E3A2B]" />
      <span className="text-sm font-semibold text-[#1C2421]">Drag and drop or choose a file</span>
      <span className="text-xs text-[#5A6561]">PDF, DOC or DOCX, up to 5 MB</span>
      <input id="caregiver-resume" type="file" accept=".pdf,.doc,.docx" className="sr-only" onChange={(e) => pick(e.target.files[0])} data-testid="caregiver-input-resume" />
    </label>
  );
};

export const CaregiverApplication = () => {
  const [form, setForm] = useState(INITIAL);
  const [file, setFile] = useState(null);
  const { submit, submitting, error, setError, reference, reset } = useSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cities = CITIES[form.region] || [];

  if (reference) {
    return (
      <SuccessPanel reference={reference} title="Thank you for applying." testId="caregiver-success" resetLabel="Submit another application"
        onReset={() => { setForm(INITIAL); setFile(null); reset(); }}>
        <p>Your application has been received and will be reviewed by our team. We contact applicants whose experience and availability match current or upcoming opportunities.</p>
        <p>Submitting an application does not guarantee an interview or a position.</p>
      </SuccessPanel>
    );
  }

  const send = () => {
    const fd = new FormData();
    fd.append("payload", JSON.stringify(buildPayload(form)));
    if (file) fd.append("resume", file);
    submit("/public/caregiver-application", fd);
  };

  const steps = [
    {
      title: "Your details", short: "Details",
      validate: () => {
        const c = validateContact({ ...form, privacy_consent: true });
        if (c) return c;
        if (!form.region) return "Please select your preferred work region.";
        return !resolveCity(form) ? "Please select or enter your city." : null;
      },
      content: (
        <>
          <ContactFields form={form} set={set} prefix="caregiver" preferred={false} />
          <Group legend="Preferred work region" required>
            <ChoiceCards options={WORK_REGIONS} value={form.region} onChange={(v) => { set("region", v); set("city", ""); }} cols="sm:grid-cols-2" testId="caregiver-region" />
          </Group>
          {form.region && cities.length > 0 && (
            <Field label="City where you live" htmlFor="caregiver-city" required>
              <NativeSelect id="caregiver-city" value={form.city} onChange={(v) => set("city", v)} options={cities} placeholder="Select your city" testId="caregiver-input-city" />
            </Field>
          )}
          {(form.region === "other" || form.city === "Other community") && (
            <TextField id="caregiver-city-custom" label="Your city or community" value={form.city_custom} onChange={(v) => set("city_custom", v)} required testId="caregiver-input-city-custom" />
          )}
        </>
      ),
    },
    {
      title: "Role and experience", short: "Role",
      validate: () => (!form.preferred_role ? "Please select a preferred role." : !form.years_experience ? "Please select your years of experience." : null),
      content: (
        <>
          <Field label="Preferred role" htmlFor="caregiver-role" required>
            <NativeSelect id="caregiver-role" value={form.preferred_role} onChange={(v) => set("preferred_role", v)} testId="caregiver-input-role"
              options={ROLES} />
          </Field>
          {form.preferred_role === ADMIN_ROLE ? (
            <Notice testId="caregiver-role-guidance">The Administrative Coordinator handles intake, responds to inquiries and emails, and manages paperwork and office support. A care certification is not required for this role.</Notice>
          ) : (
            <Notice testId="caregiver-role-guidance">Care roles require a recognized certification, such as a Personal Support Worker (PSW) certificate or an equivalent qualification accepted for in-home care in Ontario. Certificates are verified before any offer is made.</Notice>
          )}
          <Group legend="Years of relevant experience" required>
            <ChoiceCards options={YEARS} value={form.years_experience} onChange={(v) => set("years_experience", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="caregiver-years" />
          </Group>
          <TextArea id="caregiver-summary" label="Tell us about your relevant experience (optional)" value={form.experience_summary} onChange={(v) => set("experience_summary", v)}
            hint="Include paid work, placements and volunteer experience." max={1500} testId="caregiver-input-experience" />
        </>
      ),
    },
    {
      title: "Skills and qualifications", short: "Skills",
      validate: () => (form.preferred_role === ADMIN_ROLE ? null
        : form.certifications.length === 0 ? "Please select at least one certification or qualification."
        : form.certification_details.trim().length < 2 ? "Please tell us where and when you received your main certification." : null),
      content: (
        <>
          <Group legend="Experience with (select all that apply)">
            <MultiChips options={AREAS} value={form.experience_areas} onChange={(v) => set("experience_areas", v)} testId="caregiver-area" />
          </Group>
          <Group legend="Certifications and qualifications" required={form.preferred_role !== ADMIN_ROLE} hint="Select all that you hold.">
            <MultiChips options={CERTS} value={form.certifications} onChange={(v) => set("certifications", v)} testId="caregiver-cert" />
          </Group>
          <TextField id="caregiver-cert-details" label="Main certification details" value={form.certification_details} onChange={(v) => set("certification_details", v)} required={form.preferred_role !== ADMIN_ROLE}
            placeholder="e.g. PSW certificate, Humber College, 2021" hint="School or issuing organization and year completed." maxLength={400} testId="caregiver-input-cert-details" />
          <TextField id="caregiver-cert-other" label="Other certifications or training (optional)" value={form.certifications_other} onChange={(v) => set("certifications_other", v)} maxLength={400} testId="caregiver-input-cert-other" />
        </>
      ),
    },
    {
      title: "Availability", short: "Availability",
      validate: () => (form.employment_types.length === 0 ? "Please select at least one employment type." : !form.languages.trim() ? "Please list the languages you speak."
        : !form.work_authorization ? "Please answer the work authorization question." : null),
      content: (
        <>
          <Group legend="Days available"><MultiChips options={DAYS} value={form.availability_days} onChange={(v) => set("availability_days", v)} testId="caregiver-days" /></Group>
          <Group legend="Preferred shifts"><MultiChips options={SHIFTS} value={form.shifts} onChange={(v) => set("shifts", v)} testId="caregiver-shift" /></Group>
          <Group legend="Employment type" required><MultiChips options={EMPLOYMENT} value={form.employment_types} onChange={(v) => set("employment_types", v)} testId="caregiver-employment" /></Group>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Preferred hours per week" htmlFor="caregiver-hours">
              <NativeSelect id="caregiver-hours" value={form.hours_per_week} onChange={(v) => set("hours_per_week", v)} testId="caregiver-input-hours"
                options={["Under 10 hours", "10–20 hours", "20–30 hours", "30–40 hours", "40+ hours", "Flexible"]} />
            </Field>
            <TextField id="caregiver-start" label="Earliest start date (optional)" type="date" value={form.earliest_start} onChange={(v) => set("earliest_start", v)} testId="caregiver-input-start" />
          </div>
          <TextField id="caregiver-languages" label="Languages spoken" value={form.languages} onChange={(v) => set("languages", v)} required placeholder="e.g. English, Tagalog, Punjabi" testId="caregiver-input-languages" />
          <Group legend="Are you legally authorized to work in the country of your preferred region?" required>
            <ChoiceCards options={AUTH} value={form.work_authorization} onChange={(v) => set("work_authorization", v)} cols="sm:grid-cols-1" testId="caregiver-auth" />
          </Group>
        </>
      ),
    },
    {
      title: "Resume and consent", short: "Submit",
      validate: () => (!form.contact_consent ? "Please consent to be contacted about employment opportunities." : validateContact(form)),
      content: (
        <>
          <Field label="Resume (optional but recommended)" htmlFor="caregiver-resume">
            <ResumeInput file={file} setFile={setFile} setErr={setError} />
          </Field>
          <ConsentBlock form={form} set={set} prefix="caregiver" purpose="application">
            <ConsentRow id="caregiver-contact-consent" checked={form.contact_consent} onChange={(v) => set("contact_consent", v)} testId="caregiver-input-contact-consent">
              I consent to being contacted about current and future employment opportunities with Friendship Pen Care Services.<span className="text-[#B25B41]"> *</span>
            </ConsentRow>
          </ConsentBlock>
          <Honeypot value={form.website} onChange={(v) => set("website", v)} />
        </>
      ),
    },
  ];

  return <Wizard steps={steps} testPrefix="caregiver" submitting={submitting} error={error} submitLabel="Submit application" onSubmit={send} />;
};
